/**
 * Pac-Man by Oscar Wallhult
 */
import Gamerpad from 'gamerpad';

import { Direction, GameState, Key } from './engine/enums';
import { TILE_SIZE } from './maze';
import { ScreenManager } from './screens/screenManager';
import State from './state';

export default class Game {
  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly framerate: number = 1000 / 100;

  private state: State = new State();
  private screenManager: ScreenManager;
  private _lastUpdate: number = Date.now();
  private _xDown: number | null = null;
  private _yDown: number | null = null;

  constructor(selector: string, private width: number, private height: number) {
    this.container = document.querySelector(selector) as HTMLElement;
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx = this.canvas.getContext('2d')!;
    this.container.appendChild(this.canvas);

    this.screenManager = new ScreenManager(this.state, this.width, this.height);

    this.reset();
    this.bindControls();
    this.mainloop();
  }

  private reset(): void {
    this.state.reset();
    this.state.gameState = GameState.Playing;
  }

  private bindControls(): void {
    document.addEventListener('keydown', e => this.onKeyDown(e));

    document.addEventListener('touchstart', e => this.onTouchStart(e));
    document.addEventListener('touchmove', e => this.onTouchMove(e));
    document.addEventListener('touchend', e => this.onTouchEnd(e));

    Gamerpad.on('connected', evt => {
      console.log('connected', evt.gamepad);
    });

    Gamerpad.on('axis_changed', evt => {
      const player = this.state.players[evt.gamepad.index];
      let key: Key | undefined;

      switch (evt.axis) {
        case 0:
          if (evt.value === -1) {
            key = Key.Left;
          } else if (evt.value === 1) {
            key = Key.Right;
          }
          break;
        case 1:
          if (evt.value === -1) {
            key = Key.Up;
          } else if (evt.value === 1) {
            key = Key.Down;
          }
          break;
      }

      if (key !== undefined) {
        this.screenManager.getCurrentScreen().onPlayerInput(player, key, this.state);
      }
    });
  }

  private onTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const { clientX, clientY } = e.touches[0];
    this._xDown = clientX;
    this._yDown = clientY;
  }

  private onTouchEnd(e: TouchEvent): void {
  }

  private onTouchMove(e: TouchEvent): void {
    const { state } = this;
    const [player1] = state.players;
    const currentScreen = this.screenManager.getCurrentScreen();

    const { _xDown: xDown, _yDown: yDown } = this;
    if (!xDown || !yDown) {
      return;
    }

    const { clientX, clientY } = e.touches[0];
    const xDiff = xDown - clientX;
    const yDiff = yDown - clientY;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0) {
        currentScreen.onPlayerInput(player1, Key.Left, state);
      } else {
        currentScreen.onPlayerInput(player1, Key.Right, state);
      }
    } else {
      if (yDiff > 0) {
        currentScreen.onPlayerInput(player1, Key.Up, state);
      } else {
        currentScreen.onPlayerInput(player1, Key.Down, state);
      }
    }

    this._xDown = null;
    this._yDown = null;
  }

  private onKeyDown(e: KeyboardEvent): void {
    const { state } = this;
    if (!state) {
      return;
    }

    const isPlaying = this.state.gameState === GameState.Playing;
    if (e.keyCode === Key.P) {
      if (isPlaying) {
        this.state.gameState = GameState.Paused;
      } else {
        this.state.gameState = GameState.Playing;
      }
    }

    if (this.state.gameState === GameState.Paused) {
      return;
    }

    const [player1, player2] = state.players;
    const currentScreen = this.screenManager.getCurrentScreen();

    switch (e.keyCode) {
      case Key.Left:
        currentScreen.onPlayerInput(player1, Key.Left, this.state);
        break;
      case Key.Right:
        currentScreen.onPlayerInput(player1, Key.Right, this.state);
        break;
      case Key.Down:
        currentScreen.onPlayerInput(player1, Key.Down, this.state);
        break;
      case Key.Up:
        currentScreen.onPlayerInput(player1, Key.Up, this.state);
        break;

      case Key.A:
        currentScreen.onPlayerInput(player2, Key.Left, this.state);
        break;
      case Key.D:
        currentScreen.onPlayerInput(player2, Key.Right, this.state);
        break;
      case Key.S:
        currentScreen.onPlayerInput(player2, Key.Down, this.state);
        break;
      case Key.W:
        currentScreen.onPlayerInput(player2, Key.Up, this.state);
        break;
    }
  }

  private mainloop(): void {
    const deltaTime = Date.now() - this._lastUpdate;

    if (this.state.gameState !== GameState.Paused) {
      if (deltaTime >= this.framerate) {
        this._lastUpdate = Date.now();
        this.state.gameTime++;
        this.update(this.state.gameTime);
      }
    }

    this.render(this.state.gameTime, this.ctx);
    requestAnimationFrame(() => this.mainloop());
  }

  update(gameTime: number): void {
    this.screenManager.getCurrentScreen().update(gameTime, this.state);
  }


  render(gameTime: number, ctx: CanvasRenderingContext2D): void {
    this.screenManager.getCurrentScreen().render(this.state, gameTime, ctx);
  }
}
